import {watch} from "vue";
import {WrapperSubscription} from "../channel/WrapperSubscription.ts";
import useStore from "../store.ts";
import {Component} from "../types.ts";
import {areSubscriptionsDeferred} from "./ElementBuilder.ts";
import {cloneComponent, parsePx} from "./ElementUtils.ts";
import {datasheetAuthoredStart, datasheetHeight as sheetDividend, datasheetRowCapacity} from "./PureElementUtils.ts";
import {updateAnchors} from "./DataUtils.ts";
import {postDeleteItems} from "../worker/RenderWorkerManager.ts";
import {UpdateOrigin, type UpdateContextHandle} from "./UpdateContextRegistry.ts";
import {orchestrator} from "./Orchestrator.ts";

// Prefix swap without regex
const makeIdRemapper = (oldRootId: string, newRootId: string) => {
    const oldLen = oldRootId.length;
    return (oldId: string) =>
        oldId.startsWith(oldRootId) ? newRootId + oldId.slice(oldLen) : oldId;
};

type CopyOptions = {
    sheetChannel: number;
    sheetColor: string;
    sheetColors: string[];
    rowIndex: number; // index - 1
    offset: number;
    remapAdoptId: (s: string) => string;
    // Cached geometry from template root to avoid repeated parsing
    baseTop?: number;
    rowHeight?: number;
    // see docs/EXTRACTED_COMMENTS.md#c736
    resolvedHeight?: string;
    background?: string;
};

// Run immediately; avoid queuing to ensure fast coordinator handoff
const schedule = (cb: () => void) => cb();

// see docs/EXTRACTED_COMMENTS.md#c169
type SlowPathBuffer = {
    /** id → slowPathSeq at its latest (re)setup. */
    ids: Map<string, number>;
    stopWatch: () => void;
};
let slowPathBuffer: SlowPathBuffer | null = null;
let slowPathSeq = 0;

/** Mark taken by fireInitial right before it refreshes every subscription. */
export function slowPathMark(): number {
    return slowPathSeq;
}

/**
 * Drops buffered ids that fireInitial already refreshed: only ids whose latest
 * (re)setup happened before `mark`. A later setup disposes the subscription, which
 * discards fireInitial's in-flight result, so those ids keep their deferred refresh.
 */
export function dropRefreshedFromSlowPathBuffer(refreshed: Iterable<string>, mark: number): void {
    if (!slowPathBuffer) return;
    for (const id of refreshed) {
        const seq = slowPathBuffer.ids.get(id);
        if (seq != null && seq <= mark) slowPathBuffer.ids.delete(id);
    }
}

function fireSubscriptionUpdate(
    store: ReturnType<typeof useStore>,
    id: string,
    h: UpdateContextHandle,
): void {
    const item = store.components[id];
    if (!item?.subscription) return;
    item.subscription.updateData("", -1, [], h)
        .catch(err => console.warn(`[processSubsInBatches] updateData failed for ${id}:`, err));
}

function getOrCreateSlowPathBuffer(store: ReturnType<typeof useStore>): SlowPathBuffer {
    if (slowPathBuffer) return slowPathBuffer;

    const buf: SlowPathBuffer = {
        ids: new Map(),
        stopWatch: () => {},
    };
    const stop = watch(() => store.loaded, (val) => {
        if (!val) return;
        if (slowPathBuffer !== buf) return; // superseded by a later reset
        stop();
        const collected = Array.from(buf.ids.keys());
        slowPathBuffer = null;
        if (collected.length === 0) return;
        orchestrator.enqueue(UpdateOrigin.Initial, (deferredHandle) => {
            for (const id of collected) fireSubscriptionUpdate(store, id, deferredHandle);
        });
    });
    buf.stopWatch = stop;
    slowPathBuffer = buf;
    return buf;
}

/* see docs/EXTRACTED_COMMENTS.md#c548 */
export function resetSlowPathBuffer(): void {
    if (!slowPathBuffer) return;
    slowPathBuffer.stopWatch();
    slowPathBuffer = null;
}

export function processSubsInBatches(
    store: ReturnType<typeof useStore>,
    ids: string[],
    handle: UpdateContextHandle,
) {
    const setupSubscription = (id: string): boolean => {
        const item = store.components[id];
        if (!item) return false;

        const dyn = item.dynamic;
        const hasChannel = !!(dyn && (
            dyn.key_channel != null ||
            dyn.channel != null ||
            dyn.dynamic_key_channel != null ||
            dyn.dynamic_channel != null
        ));
        // see docs/EXTRACTED_COMMENTS.md#c549
        const hasAdoptDependency = !!(
            dyn?.adopt_object?.adopt_object_id ||
            dyn?.dynamic_fields?.some(df => df?.adopt_object?.adopt_object_id)
        );
        if (!hasChannel && !hasAdoptDependency) return false;

        // see docs/EXTRACTED_COMMENTS.md#c550
        const existing = item.subscription as WrapperSubscription | undefined;
        const isLoaded = !!existing && existing.componentId === id;
        if (!isLoaded) {
            item.subscription = new WrapperSubscription(id);
            // see docs/EXTRACTED_COMMENTS.md#c776
            if (!areSubscriptionsDeferred()) item.subscription.load();
        } else {
            existing.dispose?.();
        }
        return !!store.components[id]?.subscription;
    };

    const run = () => {
        const eligible: string[] = [];
        for (const id of ids) {
            if (setupSubscription(id)) eligible.push(id);
        }
        if (eligible.length === 0) return;

        // see docs/EXTRACTED_COMMENTS.md#c551
        if (store.loaded) {
            for (const id of eligible) fireSubscriptionUpdate(store, id, handle);
            return;
        }

        // see docs/EXTRACTED_COMMENTS.md#c552
        const buf = getOrCreateSlowPathBuffer(store);
        for (const id of eligible) buf.ids.set(id, ++slowPathSeq);
    };
    schedule(run);
}

function postGeometryForIdsChunked(
    store: ReturnType<typeof useStore>,
    ids: string[],
    postUpdateGeometryFn: (c: Component) => void
) {
    let idx = 0;
    const batchSize = 25;

    const step = () => {
        const end = Math.min(idx + batchSize, ids.length);
        for (; idx < end; idx++) {
            const id = ids[idx];
            const item = store.components[id];
            if (!item) continue;
            postUpdateGeometryFn(item);
        }
        if (idx < ids.length) schedule(step);
    };
    schedule(step);
}

function cloneSubtreeWithRemap(
    store: ReturnType<typeof useStore>,
    oldRootId: string,
    newRootId: string,
    created: Record<string, Component>,
    opts: CopyOptions
) {
    const { sheetChannel, sheetColor, offset, rowIndex, remapAdoptId, sheetColors } = opts;
    const remapId = makeIdRemapper(oldRootId, newRootId);

    const srcRoot = store.components[oldRootId];
    if (!srcRoot) return null;

    const root = cloneComponent(srcRoot);
    root.props.id = newRootId;
    root.props.key = newRootId;

    // see docs/EXTRACTED_COMMENTS.md#c553
    if (root.props.style) {
        const style = root.props.style;
        style.position = "absolute";
        // No theoretical fallback here: an unresolved template height means
        // ZERO spacing (rows stack at the template top; the renderer restacks
        // once real heights publish). The worst-case height (#c746) is for
        // capacity counting only and must never reach a cloned row's style.
        const height = opts.rowHeight !== undefined
            ? opts.rowHeight
            : parsePx(style.height);
        const top    = opts.baseTop !== undefined
            ? opts.baseTop
            : parsePx(style.top ?? style.height ?? "0px");
        const newTop = `${top + height * offset}px`;
        if (style.top !== newTop) style.top = newTop;
        // see docs/EXTRACTED_COMMENTS.md#c736
        if (style.height == null && opts.resolvedHeight != null) {
            style.height = opts.resolvedHeight;
        }
        if (sheetColor && !style["background-image"]) {
            style.background = sheetColor;
        }
    }
    root.dynamic.row = rowIndex;
    root.published.src = "";

    if (root.dynamic?.dynamic_fields) {
        for (const f of root.dynamic.dynamic_fields) {
            if (f?.adopt_object?.adopt_object_id) {
                f.adopt_object.adopt_object_id = remapAdoptId(f.adopt_object.adopt_object_id);
            }
        }
    }

    if (root.dynamic.adopt_object) {
        root.dynamic.adopt_object.adopt_object_id = remapAdoptId(root.dynamic.adopt_object.adopt_object_id);
    }



    // build tree from source only
    created[newRootId] = root;
    const stack: Array<{ src: Component; dst: Component }> = [{ src: srcRoot, dst: root }];

    while (stack.length) {
        const { src, dst } = stack.pop()!;
        const srcChildren = src.children ?? [];
        const newChildren: Component[] = new Array(srcChildren.length);

        for (let i = 0; i < srcChildren.length; i++) {
            const childSrc = srcChildren[i];
            const newId = remapId(childSrc.props.id);
            const child = cloneComponent(childSrc);

            child.props.id = newId;
            child.props.key = newId;
            const dot = newId.lastIndexOf(".");
            child.parent = dot === -1 ? "" : newId.slice(0, dot);

            if (sheetColor && sheetColor != "transparent") {
                if (sheetColors.find((a) => child.props.style.background?.includes(a)) || child.props.style.background == "none") {
                    child.props.style.background = sheetColor;
                    child.style.background = sheetColor;
                }
            }

            if (child.anchor && child.anchor.anchor_id?.length > 0) {
                child.anchor.anchor_id = remapAdoptId(child.anchor.anchor_id );
            }


            if (sheetChannel) {
                const kc = child.dynamic?.key_channel ?? null;
                const c  = child.dynamic?.channel ?? null;
                if ((kc ?? c) === sheetChannel){
                    child.dynamic.row = rowIndex;
                }
            }else {
                child.dynamic.row = rowIndex;
            }

            if (child.dynamic?.dynamic_fields) {
                for (const f of child.dynamic.dynamic_fields) {
                    if (f?.adopt_object?.adopt_object_id) {
                        f.adopt_object.adopt_object_id = remapAdoptId(f.adopt_object.adopt_object_id);
                    }
                }
            }


            if (child.dynamic.adopt_object) {
                child.dynamic.adopt_object.adopt_object_id = remapAdoptId(child.dynamic.adopt_object.adopt_object_id);
            }

            created[newId] = child;
            newChildren[i] = child;
            stack.push({ src: childSrc, dst: child });
        }
        dst.children = newChildren;
    }
    return root;
}

export async function buildDatasheetRowsIdle(
    item: any,
    store: ReturnType<typeof useStore>,
    postUpdateGeometryFn: (c: Component) => void,
    promise: Promise<void>,
    handle: UpdateContextHandle,
) {
    const datasheetId = item.props.id;

    // Verify we have the correct store component reference
    const storeItem = store.components[datasheetId];
    if (!storeItem) {
        console.warn(`[DatasheetBuild] buildIdle skip ${datasheetId}: storeItem missing`);
        return;
    }

    // Use the store component reference to ensure consistency
    const workingItem = storeItem;

    const colours = workingItem.datasheet_colors ?? ["transparent"];
    const channel = workingItem.dynamic?.key_channel || workingItem.dynamic?.channel || undefined;

    // Check if datasheet has valid dimensions before proceeding
    /* see docs/EXTRACTED_COMMENTS.md#c753 */
    const datasheetHeight = sheetDividend(workingItem, store.components);
    if (datasheetHeight <= 0) {
        console.warn(`[DatasheetBuild] buildIdle skip ${datasheetId}: datasheetHeight=${datasheetHeight} (style.height=${workingItem.style?.height}, auto_size=${workingItem.auto_size}) — flag reset to false`);
        // see docs/EXTRACTED_COMMENTS.md#c554
        workingItem.flags.calculated_row = false;
        return;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c747 */
    const childHeight = workingItem.children[0]?.style?.height;
    // see docs/EXTRACTED_COMMENTS.md#c555
    const calculatedLimit = datasheetRowCapacity(workingItem, store.components);
    console.log(`[DatasheetBuild] buildIdle ${datasheetId}: sheetH=${datasheetHeight}, childH=${childHeight}, limit=${calculatedLimit}, existingChildren=${workingItem.children.length}, channel=${channel}`);

    // An unresolved row height no longer refuses: datasheetRowCapacity falls
    // back to the worst-case row height (#c746), so a limit of 0 now only
    // means there is no template row to clone — a layout defect, not timing.
    if (calculatedLimit <= 0) {
        console.warn(`[DatasheetBuild] refuse ${datasheetId}: no template row to clone`
            + ` (sheetH=${datasheetHeight}px) — nothing built, calculated_row reset`);
        workingItem.flags.calculated_row = false;
        return;
    }

    if (item.children.length >= calculatedLimit) {
        console.warn(`[DatasheetBuild] early-return ${datasheetId}: children=${item.children.length} >= limit=${calculatedLimit} (sheetH=${datasheetHeight}, childH=${childHeight}) — calculated_row remains true`);
        return;
    }

    const limit = calculatedLimit;


    let min = Infinity;
    let max = -Infinity;
    for (const c of workingItem.children) {
        const splitComponents = c.props.id.split(".");
        const trailerId = Number(splitComponents[splitComponents.length - 1]?.replace(/^0+/, ""));
        min = Math.min(trailerId, min);
        max = Math.max(trailerId, max);
    }

    const sheetChildren: any[] = [];
    const tasks = Array.from({length: limit}, (_, idx) => idx + 1);

    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    /* see docs/EXTRACTED_COMMENTS.md#c764 */
    let start_idx = (store.socketManager.layoutManager?.startRowFor(datasheetId) ?? 0)
        + (datasheetAuthoredStart(workingItem) ?? 0);

    for (let i of tasks) {
        const color = colours[(i - 1) % colours.length];
        handleDatasheetRow(workingItem.children, channel!, color, i, max, colours, promise, postUpdateGeometryFn, handle, start_idx);

        let formatStr = "";
        if (i + max <= 9) formatStr = "0";
        formatStr += `${i + max}`;

        const childId = item.props.id + "." + formatStr;

        // see docs/EXTRACTED_COMMENTS.md#c556
        const storeChild = store.components[childId];
        if (storeChild) {
            sheetChildren.push(storeChild);
        }

        // see docs/EXTRACTED_COMMENTS.md#c557
        await Promise.resolve();
    }

    const deleteIds = collectOrphans(item);

    await postDeleteItems([...deleteIds]);

    updateAnchors();

    // see docs/EXTRACTED_COMMENTS.md#c558
    workingItem.children = sheetChildren;
    // see docs/EXTRACTED_COMMENTS.md#c559
    // see docs/EXTRACTED_COMMENTS.md#c560
    /* see docs/EXTRACTED_COMMENTS.md#c749 */
    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    await store.socketManager.layoutManager?.onDatasheetBuilt(datasheetId);
    postUpdateGeometryFn(workingItem);
}


export function collectOrphans(item: Component): string[] {
    const store = useStore();
    const templateId = item.children[0].props.id;

    const templateItem = store.components[templateId];
    if (!templateItem)  return [];
    const deleteIds: string[] = [];
    if (templateItem) {
        const stack = [templateItem];
        while (stack.length) {
            const child = stack.pop()!;
            const childId = typeof child === "string" ? child : child.props?.id;
            if (!childId) continue;
            deleteIds.push(childId);
            const childComp = store.components[childId];
            if (childComp?.children) stack.push(...childComp.children);
        }
    }
    return deleteIds;
}

export function handleDatasheetRow(
    elements: Component[],
    sheetChannel: number,
    sheetColor: string,
    index: number,
    padding: number,
    sheetColors: string[],
    promise: Promise<void>,
    postUpdateGeometryFn: (c: Component) => void,
    handle: UpdateContextHandle,
    start_idx: number,
) {
    const store = useStore();
    const rowIndex = (index - 1) + start_idx;
    const offset = index - 1;
    const created: Record<string, Component> = {};
    const newRootIds: string[] = [];

    for (const el of elements) {
        const trailer = index + padding;
        const last = el.props.id.lastIndexOf(".");
        const prefix = last === -1 ? "" : el.props.id.slice(0, last + 1);
        const padded = String(trailer).padStart(2, '0');
        const newId = `${prefix}${padded}`;
        if (el.props.id === newId) continue;

        const remapAdoptId = (s: string) =>
            s.startsWith(el.props.id) ? newId + s.slice(el.props.id.length) : s;


        // see docs/EXTRACTED_COMMENTS.md#c561
        let baseTop: number | undefined = undefined;
        let rowHeight: number | undefined = undefined;
        let resolvedHeight: string | undefined = undefined;
        const template = store.components[el.props.id];
        if (template?.props?.style) {
            const style = template.props.style as any;
            const topPx = style.top ?? style.height ?? "0px";
            const borderHeight = parsePx(style['border-top-width']) + parsePx(style['border-bottom-width']);
            // The worst-case capacity height (#c746) is a counting device only:
            // an unresolved template height contributes ZERO spacing — rows
            // stack at the template top and the renderer restacks them once
            // real heights publish.
            const parsedHeight = parsePx(style.height);
            rowHeight = parsedHeight > 0 ? parsedHeight + borderHeight : 0;
            baseTop   = +parsePx(topPx);
            // see docs/EXTRACTED_COMMENTS.md#c736
            resolvedHeight = typeof style.height === "string" ? style.height : undefined;
        }

        el.dynamic.row = rowIndex;

        cloneSubtreeWithRemap(store, el.props.id, newId, created, {
            sheetChannel,
            sheetColor,
            rowIndex,
            offset,
            remapAdoptId,
            baseTop,
            rowHeight,
            resolvedHeight,
            sheetColors
        });

        newRootIds.push(newId);
    }

    // see docs/EXTRACTED_COMMENTS.md#c562
    for (const [id, comp] of Object.entries(created)) {
        store.components[id] = comp;
    }
    promise.then(() => {
        const createdIds = Object.keys(created);
        processSubsInBatches(store, createdIds, handle);
        postGeometryForIdsChunked(store, createdIds, postUpdateGeometryFn);
    });
    return;
}
